import React, { useMemo, useRef, useState } from 'react';
import { Table, FilterBuilder, FieldType, Flex } from 'onefaceui-react-components';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';

import queryClient from 'react/services/queryClient';
import defaultFetch from 'react/api/sagas/fetch/defaultFetch';
import { getResourceTo } from 'react/utils/utils';

import { useAuthCredentials } from 'react/queries/AuthCredentials';
import { AuthMethods } from './AuthorizationCredentialForm';
import { Container } from './AuthorizationCredentialStyles';
import EditIcon from 'static/images/monet/edit.svg';

export function AutorizationCredentialList() {
  const dispatch = useDispatch();
  const tableRef = useRef(null);
  const { data = [], isFetching } = useAuthCredentials();
  const [filters, setFilters] = useState({});

  const tableColumns = [
    {
      title: getResourceTo('authCredentials.fields.description'),
      field: 'name'
    },
    {
      title: getResourceTo('authCredentials.fields.method'),
      field: 'type.engineType'
    }
  ];

  const filterBuilderFields = [
    {
      id: 'name',
      label: getResourceTo('authCredentials.fields.description'),
      name: 'name',
      type:  FieldType.TEXT,
    },
    {
      id: 'type.engineType',
      label: getResourceTo('authCredentials.fields.method'),
      name: 'type.engineType',
      type:  FieldType.SELECT,
      extra: {
        options: [
          {
            label: getResourceTo('authCredentials.methods.noAuth'),
            value: AuthMethods.NOAUTH
          },
          {
            label: getResourceTo('authCredentials.methods.basicAuth'),
            value: AuthMethods.BASIC
          },
          {
            label: getResourceTo('authCredentials.methods.bearerToken'),
            value: AuthMethods.BEARER_TOKEN
          },
          {
            label: getResourceTo('authCredentials.methods.apiKey'),
            value: AuthMethods.API_KEY
          },
          {
            label: getResourceTo('authCredentials.methods.OAuth'),
            value: AuthMethods.OAUTH
          }
        ]
      }
    }
  ];

  const deleteItem = async (idList) => {
    const [id] = idList;

    if (id) {
      tableRef.current?.setLoading(true);
      await defaultFetch(`AuthConfig?id=${id}`, {}, 'DELETE');
      queryClient.setQueryData(['auth-credentials'], (dataCache) => dataCache.filter((item) => item.id !== id));

      tableRef.current?.setLoading(false);
    }
  };

  const handleClickDelete = (values) => {
    const idsList = values.map(({ id }) => id);
    new Greenmile.Components.AlertModal(
      'Greenmile.Grid.BatchDelete.confirmDeletion',
      'Greenmile.Grid.BatchDelete.confirmDeletion.header',
      () => deleteItem(idsList),
      null,
      {
        isADeleteAction: true
      }
    ).show();
  };

  const getDataFiltered = () => {
    const filterValues = Object.values(filters).filter((val) => val);

    if (!filterValues.length) return data;

    const filterData = (authItem) => Object.entries(filters)
      .every(([key, value]) => {
        if(key === 'type.engineType') {
          return new RegExp(value, 'i').test(authItem.type.engineType)
        }
        return new RegExp(value, 'i').test(authItem[key])
      });

    const filteredData = data.filter(filterData);

    return filteredData;
  };

  const dataFiltered = getDataFiltered();

  const tableActions = useMemo(() => {
    const actions = {
      customizeColumns: false
    };

    if (Monet.Security.checkKeyIsEnabled(['AUTHENTICATIONMETHOD_CREATE'])) {
      actions.create = () => {
        dispatch(push('/AuthorizationCredential/new'));
      }
    }

    if (Monet.Security.checkKeyIsEnabled(['AUTHENTICATIONMETHOD_DELETE'])) {
      actions.delete = (values) => {
        if (values.length) handleClickDelete(values);
      }
    }

    return actions;
  }, []);

  const tableCustomColumns = useMemo(() => {
    const columns = [];

    if (Monet.Security.checkKeyIsEnabled(['AUTHENTICATIONMETHOD_UPDATE'])) {
      columns.push({
        width: 50,
        field: 'edit',
        frozen: true,
        headerSort: false,
        hozAlign: 'center',
        formatter() {
          return `<img src=${EditIcon} class="edit-icon" alt="Edit Icon" />`;
        },
        cellClick(_, cell) {
          const { id } = cell.getRow().getData();
          dispatch(push(`/AuthorizationCredential/edit/${id}`));
        }
      })
    }

    return columns;
  }, []);

  return (
    <Container data-testid="authorization-credential-list">
      <FilterBuilder
        defaultSelected={['name']}
        onSubmit={(values) => setFilters(values)}
        fields={filterBuilderFields}
      />
      <Flex className="oneface-table-container">
        <Table
          isLoading={isFetching}
          ref={tableRef}
          defaultData={dataFiltered}
          tableOptions={{ selectableRows: 1 }}
          headerOptions={{
            showResults: true,
            actions: tableActions
          }}
          customColumns={tableCustomColumns}
          columns={tableColumns}
        />
      </Flex>
    </Container>
  );
}
